import driverMetricDefinitions from 'data/driverMetricDefinitions.json';

// Special metrics are static (not stored in Firestore), so the cache
// should treat their IDs as always resolved and never attempt to fetch them.
export const SPECIAL_METRIC_IDS = new Set(
  driverMetricDefinitions.map((d: { id: string }) => d.id),
);

/**
 * Whether a metric id has reached a terminal (resolved) state for gating.
 *
 * A normal / special / errored id is resolved once it is present (or known to
 * have failed). A COMPOUND metric is only resolved when its own doc is cached
 * AND every input metric it references has also reached a terminal state — the
 * compound's inputs load on a lagging pass after the compound doc itself, so
 * treating the compound as resolved the moment its doc is cached lets gates
 * open too early. The request then ships the full expression with a partial
 * `metrics` list, which the backend rejects ("parse error: Expected EOF").
 *
 * We check one level deep only (a child that is itself a cached compound counts
 * as resolved); an errored/deleted input still counts as terminal so a
 * genuinely missing input can't block a gate forever.
 */
const getIsIdResolved = ({
  id,
  normalMetricsCache,
  compoundMetricsCache,
  erroredIds,
}: {
  id: string;
  normalMetricsCache: Map<string, Metrics.NormalMetric>;
  compoundMetricsCache: Map<string, Metrics.CompoundMetric>;
  erroredIds: Set<string>;
}): boolean => {
  if (
    normalMetricsCache.has(id) ||
    erroredIds.has(id) ||
    SPECIAL_METRIC_IDS.has(id)
  ) {
    return true;
  }

  const compound = compoundMetricsCache.get(id);
  if (compound) {
    return compound.metricIds.every(
      (childId) =>
        normalMetricsCache.has(childId) ||
        compoundMetricsCache.has(childId) ||
        erroredIds.has(childId) ||
        SPECIAL_METRIC_IDS.has(childId),
    );
  }

  return false;
};

export default getIsIdResolved;
